import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";

const cache = new Map();
const pending = new Map();
const queue = [];
const MAX_BYTES = 12 * 1024 * 1024;
let bytes = 0;
let active = 0;
export const ingredientImageKey = (ingredient) => JSON.stringify([
  ingredient.imageScope || "", ingredient.imageCompanyId, ingredient.id, ingredient.imageVersion || "",
]);

const removeCached = (key) => {
  bytes -= cache.get(key)?.url?.length || 0;
  cache.delete(key);
};

export const invalidateIngredientImage = (companyId, id) => {
  for (const key of new Set([...cache.keys(), ...pending.keys()])) {
    const [, company, ingredient] = JSON.parse(key);
    if (company === companyId && ingredient === id) {
      removeCached(key);
      pending.delete(key);
    }
  }
};

export const getCachedIngredientImage = (ingredient) => {
  const key = ingredientImageKey(ingredient);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) { removeCached(key); return undefined; }
  cache.delete(key);
  cache.set(key, entry);
  return entry.url;
};

const releaseSlot = () => { active -= 1; drain(); };

const drain = () => {
  // Limit photos to two requests so they cannot saturate stock/menu operations.
  while (active < 2 && queue.length) {
    active += 1;
    const { run, resolve, reject } = queue.shift();
    run().then(resolve, reject).finally(releaseSlot);
  }
};

export const loadIngredientImage = (ingredient) => {
  if (!ingredient.imageCompanyId || !ingredient.id) return Promise.resolve(null);
  const cached = getCachedIngredientImage(ingredient);
  if (cached !== undefined) return Promise.resolve(cached);
  const key = ingredientImageKey(ingredient);
  if (pending.has(key)) return pending.get(key);
  const request = new Promise((resolve, reject) => {
    queue.push({ resolve, reject, run: () => withRequestDeadline(async (signal) => {
      const { data, error } = await supabase.from("ingredients").select("image_url")
        .eq("company_id", ingredient.imageCompanyId).eq("id", ingredient.id)
        .maybeSingle().abortSignal(signal);
      if (error) throw new Error(error.message || "No se pudo cargar la foto.");
      const url = data?.image_url || null;
      if (!signal.aborted && pending.get(key) === request && (url?.length || 0) <= MAX_BYTES) {
        removeCached(key);
        cache.set(key, { url, expiresAt: Date.now() + 60000 });
        bytes += url?.length || 0;
        while (bytes > MAX_BYTES || cache.size > 100) removeCached(cache.keys().next().value);
      }
      return url;
    }, { timeoutMs: 15000, message: "La foto tardo demasiado en cargar." }) });
  }).finally(() => { if (pending.get(key) === request) pending.delete(key); });
  pending.set(key, request);
  drain();
  return request;
};
